import {startJourneyEvents} from '../services/journeyEvents';
import React, { useState, useEffect, useRef } from 'react';
import { Alert, Box, CircularProgress } from '@mui/material';
import { FaceVideoMocap } from './FaceVideoMocap';
import { features, disabledFeatures } from '../services/captureAccount';
import { AIVideo } from './AIVideo';
import { AnimationStudio } from './AnimationStudio';
import { TextAnimation } from './TextAnimation';
import { BodyPoseMocap } from './BodyPoseMocap';
import { LoginPage } from './LoginPage';
import { MainPage } from './MainPage';
import { ProductsPage } from './ProductsPage';
import { PaymentSuccessPage } from './PaymentSuccessPage';
import { PaymentCancelPage } from './PaymentCancelPage';
import { UnityDocsPage } from './UnityDocsPage';
import { UserAccount } from './UserAccount';
import { TermsPage } from './TermsPage';
import { PrivacyPage } from './PrivacyPage';
import { RefundPage } from './RefundPage';
import { Header } from './Header';
import { Footer } from './Footer';
import { authService } from '../services/auth';
import {startClientDiagnostics} from '../services/clientDiagnostics';

type PageView = 'main' | 'login' | 'capture' | 'products' | 'payment-success' | 'payment-cancel' | 'unity-docs' | 'account' | 'terms' | 'privacy' | 'refund' | 'body-pose-mocap' | 'capture-live' | 'ai-video' | 'animation-studio' | 'text-animation';

const routes: Record<string, PageView> = {
  '/': 'main', '/login': 'login', '/plans': 'products', '/unity-docs': 'unity-docs',
  '/capture': 'capture', '/body-pose-mocap': 'body-pose-mocap', '/capture-live': 'capture',
  '/account': 'account', '/text-animation': 'text-animation', '/animation-studio': 'animation-studio',
  '/ai-video': 'ai-video', '/terms_conditions': 'terms', '/privacy_policy': 'privacy',
  '/refund_policy': 'refund', '/payment/success': 'payment-success', '/payment/cancel': 'payment-cancel',
};
const privatePages: PageView[] = ['text-animation', 'animation-studio', 'ai-video', 'capture-live', 'account'];
const pageFromLocation = (): PageView => {
  const page = routes[window.location.pathname];
  if (page && page !== 'main') return page;
  return window.location.search.includes('paymentId') && window.location.search.includes('PayerID') ? 'payment-success' : page || 'main';
};

const App: React.FC = () => {
  useEffect(()=>startClientDiagnostics(),[]);
  useEffect(()=>startJourneyEvents(),[]);
  const [capabilities, setCapabilities] = useState(disabledFeatures);
  const [featuresLoading, setFeaturesLoading] = useState(true);
  useEffect(() => {
    const controller = new AbortController();
    features(controller.signal).then(setCapabilities).catch(() => {}).finally(() => {if (!controller.signal.aborted) setFeaturesLoading(false);});
    return () => controller.abort();
  }, []);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [username, setUsername] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<PageView>('main');
  const returnAfterLogin = useRef<PageView>('main');
  const plansReturnSearch = useRef('');

  useEffect(() => {
    const checkAuth = async () => {
      let authenticated = false;
      if (authService.isAuthenticated()) {
        try {
          const user = await authService.getCurrentUser();
          setUsername(user.username);
          authenticated = true;
        } catch {
          authService.logout();
        }
      }
      setIsAuthenticated(authenticated);
      const page = pageFromLocation();
      const requested = routes[new URLSearchParams(window.location.search).get('next') || ''];
      if (page === 'login' && requested && (privatePages.includes(requested) || ['capture', 'body-pose-mocap', 'products'].includes(requested))) {
        returnAfterLogin.current = requested;
      }
      if (!authenticated && privatePages.includes(page)) {
        returnAfterLogin.current = page;
        setCurrentPage('login');
      } else setCurrentPage(page);
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  const handleLoginSuccess = async () => {
    try {
      const user = await authService.getCurrentUser();
      setUsername(user.username);
      setIsAuthenticated(true);
      const destination = returnAfterLogin.current;
      returnAfterLogin.current = 'main';
      setCurrentPage(destination);
      window.history.pushState({}, '', destination === 'main' ? '/' : destination === 'products' ? '/plans' + plansReturnSearch.current : `/${destination}`);
    } catch (error) {
      console.error('Failed to get user info:', error);
    }
  };

  const handleLogout = () => {
    authService.logout();
    setIsAuthenticated(false);
    setUsername('');
    setCurrentPage('main');
    window.history.pushState({}, '', '/');
    // Dispatch logout event to clear subscription context
    window.dispatchEvent(new Event('logout'));
  };

  const handleStartAIVideo = () => {
    if (!isAuthenticated) {
      returnAfterLogin.current = 'ai-video';
      setCurrentPage('login');
      window.history.pushState({}, '', '/login');
      return;
    }
    setCurrentPage('ai-video');
    window.history.pushState({}, '', '/ai-video');
  };

  const handleStartTextAnimation = () => {
    if (!isAuthenticated) {
      returnAfterLogin.current = 'text-animation';
      setCurrentPage('login');
      window.history.pushState({}, '', '/login');
      return;
    }
    setCurrentPage('text-animation');
    window.history.pushState({}, '', '/text-animation');
  };

  const handleStartAnimationStudio = () => {
    if (!isAuthenticated) {
      returnAfterLogin.current = 'animation-studio';
      setCurrentPage('login');
      window.history.pushState({}, '', '/login');
      return;
    }
    setCurrentPage('animation-studio');
    window.history.pushState({}, '', '/animation-studio');
  };

  const handleExploreBodyPose = () => {
    setCurrentPage('body-pose-mocap');
    window.history.pushState({}, '', '/body-pose-mocap');
  };
  const handleExploreFace = () => {
    setCurrentPage('capture');
    window.history.pushState({}, '', '/capture');
  };

  const handleBodyPoseLogin = () => {
    returnAfterLogin.current = 'body-pose-mocap';
    authService.logout();
    setIsAuthenticated(false);
    setUsername('');
    setCurrentPage('login');
    window.history.pushState({}, '', '/login?next=/body-pose-mocap');
  };

  const handleBackToMain = () => {
    setCurrentPage('main');
    window.history.pushState({}, '', '/');
  };

  const handleShowProducts = () => {
    setCurrentPage('products');
    window.history.pushState({}, '', '/plans');
  };

  const handleShowLogin = () => {
    setCurrentPage('login');
    window.history.pushState({}, '', '/login');
  };

  const handleShowUnityDocs = () => {
    setCurrentPage('unity-docs');
    window.history.pushState({}, '', '/unity-docs');
  };

  const handleShowAccount = () => {
    // Require login for Account page
    if (!isAuthenticated) {
      returnAfterLogin.current = 'account';
      setCurrentPage('login');
      window.history.pushState({}, '', '/login');
      return;
    }
    setCurrentPage('account');
    window.history.pushState({}, '', '/account');
  };

  const handleShowTerms = () => {
    setCurrentPage('terms');
    window.history.pushState({}, '', '/terms_conditions');
  };

  useEffect(() => {
    if (isLoading) return;
    const handlePopState = () => {
      const page = pageFromLocation();
      if (!isAuthenticated && privatePages.includes(page)) {
        returnAfterLogin.current = page;
        setCurrentPage('login');
      } else setCurrentPage(page);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isAuthenticated, isLoading]);

  if (isLoading || featuresLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Header 
        walletEnabled={capabilities.wallet}
        username={isAuthenticated ? username : undefined} 
        onLogout={handleLogout} 
        onLogin={handleShowLogin}
        onHomeClick={handleBackToMain}
        onProductsClick={handleShowProducts}
        onUnityDocsClick={handleShowUnityDocs}
        onAccountClick={handleShowAccount}
        onBodyPoseClick={handleExploreBodyPose}
        onAIVideoClick={capabilities.ai_video ? handleStartAIVideo : undefined}
        onTextAnimationClick={capabilities.text_animation ? handleStartTextAnimation : undefined}
        onAnimationStudioClick={capabilities.animation_studio ? handleStartAnimationStudio : undefined}
        onFaceClick={handleExploreFace}
      />
      
      <Box sx={{ flex: 1 }}>
        {currentPage === 'login' && <LoginPage onLoginSuccess={handleLoginSuccess} context={returnAfterLogin.current === 'body-pose-mocap' ? 'Sign in to download your body animation. Your temporary results and credits will be preserved.' : returnAfterLogin.current === 'capture' ? 'Sign in to download your facial animation. Your temporary results and credits will be preserved.' : returnAfterLogin.current === 'products' ? 'Sign in to continue from credits and subscriptions.' : undefined} />}
        {currentPage === 'capture' && (capabilities.face_video ? <FaceVideoMocap liveEnabled={false} onBack={handleBackToMain} onLive={handleBackToMain} onLogin={() => {returnAfterLogin.current='capture';setCurrentPage('login');window.history.pushState({},'', '/login?next=/capture');}} /> : <Alert severity="info">Recorded facial capture is not enabled in this environment. Local EmotionCap versions remain available independently.</Alert>)}

        {currentPage === 'text-animation' && isAuthenticated && <TextAnimation onBack={handleBackToMain} />}
        {currentPage === 'animation-studio' && isAuthenticated && capabilities.animation_studio && <AnimationStudio onBack={handleBackToMain} />}
        {currentPage === 'ai-video' && isAuthenticated && capabilities.ai_video && <AIVideo onBack={handleBackToMain} />}
        {currentPage === 'body-pose-mocap' && <BodyPoseMocap onBack={handleBackToMain} onLogin={handleBodyPoseLogin} />}
        {currentPage === 'products' && <ProductsPage onBack={handleBackToMain} onLogin={() => {returnAfterLogin.current = 'products'; plansReturnSearch.current = window.location.search; handleShowLogin();}} />}
        {currentPage === 'account' && <UserAccount onBack={handleBackToMain} walletEnabled={capabilities.wallet} />}
        {currentPage === 'payment-success' && <PaymentSuccessPage onBackToHome={handleBackToMain} />}
        {currentPage === 'payment-cancel' && (
          <PaymentCancelPage 
            onBackToHome={handleBackToMain}
            onBackToProducts={handleShowProducts}
          />
        )}
        {currentPage === 'unity-docs' && <UnityDocsPage />}
        {currentPage === 'terms' && <TermsPage />}
        {currentPage === 'privacy' && <PrivacyPage />}
        {currentPage === 'refund' && <RefundPage />}
        {currentPage === 'main' && <MainPage faceVideoEnabled={capabilities.face_video} onStartCapture={handleExploreFace} onStartBodyPose={handleExploreBodyPose} onPlans={handleShowProducts} />}
      </Box>

      <Footer onTermsClick={handleShowTerms} />
    </Box>
  );
};

export default App;